import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormControl
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoModule } from '@jsverse/transloco';
import { filter, switchMap } from 'rxjs/operators';
import {
    ClientService,
    PendingProviderSelectionService
} from 'src/app/core/services';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import { Program } from '../../../program/models/program.model';
import { ProgramService } from '../../../program/services/program.service';
import { ProviderSearch } from '../../models';

@Component({
    selector: 'app-provider-search',
    templateUrl: './provider-search.component.html',
    styleUrls: ['./provider-search.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        TranslocoModule,
        ReactiveFormsModule,
        FlexLayoutModule,
        MatButtonModule,
        MatSelectModule,
        FormsModule,
        MatIcon,
        MatChipsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule
    ]
})
export class ProviderSearchComponent implements OnInit {
    @Output()
    searchEvent: EventEmitter<ProviderSearch> =
        new EventEmitter<ProviderSearch>();

    @Input()
    set formattedSearchAddress(value: string | undefined) {
        if (value && this.searchParams.address != value)
            this.searchParams.address = value;
    }

    programs: Program[] | undefined;
    selectedPrograms: Program[] | undefined;
    distanceSearch: string | undefined;
    searchParams: ProviderSearch = new ProviderSearch();
    programsControl: UntypedFormControl = new UntypedFormControl();

    constructor(
        private _programs: ProgramService,
        private _googleAnalyticsService: GoogleAnalyticsService,
        private _client: ClientService,
        private readonly _pendingSelection: PendingProviderSelectionService
    ) {}

    ngOnInit(): void {
        this._googleAnalyticsService.pageView('Provider Search');
        this._client.latest
            .pipe(
                filter((client) => client !== null && client !== undefined),
                switchMap(() => this._programs.getPrograms())
            )
            .subscribe((result) => {
                this.programs = result;
                this.setDefaultProgramSelectionFromProgramPage(this.programs);
                // search() writes or removes the staged program ids from whatever ends up
                // selected, so it settles the key either way.
                this.search();
            });
    }

    clearProviderSearch() {
        this.searchParams.searchText = '';
        this.search();
    }
    clearAddress() {
        this.searchParams.address = '';
        this.search();
    }

    search() {
        this._googleAnalyticsService.search(
            'Provider Search',
            this.searchParams.searchText,
            this.searchParams.address?.length > 0,
            this.searchParams.distance
        );

        this.searchParams.programIds = [];
        this.selectedPrograms?.forEach((x) => {
            if (x.programId) {
                this.searchParams.programIds?.push(x.programId);
            }
        });

        this.searchParams.distance = this.distanceSearch
            ? parseInt(this.distanceSearch)
            : 0;

        this._pendingSelection.setProgramIds(this.searchParams.programIds);

        this.searchEvent.emit(this.searchParams);
    }

    removeProgram(index: number) {
        this.selectedPrograms?.splice(index, 1);
        this.programsControl?.setValue(this.selectedPrograms);
        this.search();
    }

    private setDefaultProgramSelectionFromProgramPage(programs: Program[]) {
        // Programs chosen on the Program page, consumed as we read them — the filter is not meant
        // to survive the first search.  Empty when the student came straight to the provider page.
        const idsFromProgramPage =
            this._pendingSelection.takeProgramFilterIds();
        if (idsFromProgramPage.length === 0) return;

        this.selectedPrograms = programs.filter(
            (x) => x.programId && idsFromProgramPage.includes(x.programId)
        );

        this.programsControl?.setValue(this.selectedPrograms);
    }
}
